/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { ProductTypeEnum } from './ProductTypeEnum';
import {
    ProductTypeEnumFromJSON,
    ProductTypeEnumFromJSONTyped,
    ProductTypeEnumToJSON,
} from './ProductTypeEnum';
import type { CurrencyRate } from './CurrencyRate';
import {
    CurrencyRateFromJSON,
    CurrencyRateFromJSONTyped,
    CurrencyRateToJSON,
} from './CurrencyRate';
import type { Issuer } from './Issuer';
import {
    IssuerFromJSON,
    IssuerFromJSONTyped,
    IssuerToJSON,
} from './Issuer';
import type { ProductPermissionEnum } from './ProductPermissionEnum';
import {
    ProductPermissionEnumFromJSON,
    ProductPermissionEnumFromJSONTyped,
    ProductPermissionEnumToJSON,
} from './ProductPermissionEnum';
import type { TokenContractInfo } from './TokenContractInfo';
import {
    TokenContractInfoFromJSON,
    TokenContractInfoFromJSONTyped,
    TokenContractInfoToJSON,
} from './TokenContractInfo';
import type { DistributionPlatform } from './DistributionPlatform';
import {
    DistributionPlatformFromJSON,
    DistributionPlatformFromJSONTyped,
    DistributionPlatformToJSON,
} from './DistributionPlatform';
import type { VersionedIssuanceDocument } from './VersionedIssuanceDocument';
import {
    VersionedIssuanceDocumentFromJSON,
    VersionedIssuanceDocumentFromJSONTyped,
    VersionedIssuanceDocumentToJSON,
} from './VersionedIssuanceDocument';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';

/**
 * 
 * @export
 * @interface Product
 */
export interface Product {
    /**
     * 
     * @type {Document}
     * @memberof Product
     */
    backToBack: Document;
    /**
     * Defines whether commitment requires a bank account for investor
     * @type {boolean}
     * @memberof Product
     */
    readonly bankAccountRequired: boolean;
    /**
     * Shall accrued interests be calculated for investments and shown in payment details?
     * @type {boolean}
     * @memberof Product
     */
    readonly calculateAccruedInterest: boolean;
    /**
     * 
     * @type {Date}
     * @memberof Product
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {Array<CurrencyRate>}
     * @memberof Product
     */
    readonly currencies: Array<CurrencyRate>;
    /**
     * 
     * @type {Array<DistributionPlatform>}
     * @memberof Product
     */
    readonly distributionPlatforms: Array<DistributionPlatform>;
    /**
     * 
     * @type {string}
     * @memberof Product
     */
    readonly entryType: string;
    /**
     * This is a product with an eWpG issuance. That means we need to create registry statements
     * @type {boolean}
     * @memberof Product
     */
    readonly ewpg: boolean;
    /**
     * 
     * @type {string}
     * @memberof Product
     */
    readonly id: string;
    /**
     * Deadline for the investor making the bank transfer (in days)
     * @type {number}
     * @memberof Product
     */
    readonly investmentPaymentDeadline: number;
    /**
     * Defines whether this token complies with ECSP.
     * @type {boolean}
     * @memberof Product
     */
    readonly isEcsp: boolean;
    /**
     * 
     * @type {boolean}
     * @memberof Product
     */
    readonly isTokenized: boolean;
    /**
     * ISIN of security
     * @type {string}
     * @memberof Product
     */
    readonly isin: string | null;
    /**
     * 
     * @type {VersionedIssuanceDocument}
     * @memberof Product
     */
    readonly issuanceDocument: VersionedIssuanceDocument;
    /**
     * 
     * @type {Issuer}
     * @memberof Product
     */
    issuer: Issuer;
    /**
     * 
     * @type {Array<Document>}
     * @memberof Product
     */
    readonly issuerDocuments: Array<Document>;
    /**
     * 
     * @type {Money}
     * @memberof Product
     */
    readonly latestProductPrice: Money;
    /**
     * 
     * @type {boolean}
     * @memberof Product
     */
    readonly managed: boolean;
    /**
     * 
     * @type {number}
     * @memberof Product
     */
    maxIssuableAmount: number;
    /**
     * 
     * @type {string}
     * @memberof Product
     */
    readonly name: string;
    /**
     * 
     * @type {Money}
     * @memberof Product
     */
    nominalValue: Money;
    /**
     * 
     * @type {Array<ProductPermissionEnum>}
     * @memberof Product
     */
    readonly permissions: Array<ProductPermissionEnum>;
    /**
     * Type of financial product
     * @type {ProductTypeEnum}
     * @memberof Product
     */
    readonly productType: ProductTypeEnum;
    /**
     * Indicates whether the most recent publication is valid. A publication turns invalid if no investment was made during the first 3 months after the creation of the crypto security
     * @type {boolean}
     * @memberof Product
     */
    readonly publicationValid: boolean | null;
    /**
     * the number of units that are untouched, i.e. neither used in invitations, fulfilled nor issued
     * @type {number}
     * @memberof Product
     */
    readonly remainingAmount: number;
    /**
     * Defines whether commitment requires a securities depot for investor
     * @type {boolean}
     * @memberof Product
     */
    readonly securitiesDepositAccountRequired: boolean;
    /**
     * 
     * @type {string}
     * @memberof Product
     */
    readonly tokenType: string;
    /**
     * 
     * @type {TokenContractInfo}
     * @memberof Product
     */
    tokenizationInfo: TokenContractInfo | null;
    /**
     * 
     * @type {number}
     * @memberof Product
     */
    readonly totalVolume: number;
    /**
     * 
     * @type {Document}
     * @memberof Product
     */
    vib: Document;
    /**
     * URL to the product's website
     * @type {string}
     * @memberof Product
     */
    readonly website: string | null;
}



/**
 * Check if a given object implements the Product interface.
 */
export function instanceOfProduct(value: object): value is Product {
    if (!('backToBack' in value) || value['backToBack'] === undefined) return false;
    if (!('bankAccountRequired' in value) || value['bankAccountRequired'] === undefined) return false;
    if (!('calculateAccruedInterest' in value) || value['calculateAccruedInterest'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('currencies' in value) || value['currencies'] === undefined) return false;
    if (!('distributionPlatforms' in value) || value['distributionPlatforms'] === undefined) return false;
    if (!('entryType' in value) || value['entryType'] === undefined) return false;
    if (!('ewpg' in value) || value['ewpg'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('investmentPaymentDeadline' in value) || value['investmentPaymentDeadline'] === undefined) return false;
    if (!('isEcsp' in value) || value['isEcsp'] === undefined) return false;
    if (!('isTokenized' in value) || value['isTokenized'] === undefined) return false;
    if (!('isin' in value) || value['isin'] === undefined) return false;
    if (!('issuanceDocument' in value) || value['issuanceDocument'] === undefined) return false;
    if (!('issuer' in value) || value['issuer'] === undefined) return false;
    if (!('issuerDocuments' in value) || value['issuerDocuments'] === undefined) return false;
    if (!('latestProductPrice' in value) || value['latestProductPrice'] === undefined) return false;
    if (!('managed' in value) || value['managed'] === undefined) return false;
    if (!('maxIssuableAmount' in value) || value['maxIssuableAmount'] === undefined) return false;
    if (!('name' in value) || value['name'] === undefined) return false;
    if (!('nominalValue' in value) || value['nominalValue'] === undefined) return false;
    if (!('permissions' in value) || value['permissions'] === undefined) return false;
    if (!('productType' in value) || value['productType'] === undefined) return false;
    if (!('publicationValid' in value) || value['publicationValid'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('securitiesDepositAccountRequired' in value) || value['securitiesDepositAccountRequired'] === undefined) return false;
    if (!('tokenType' in value) || value['tokenType'] === undefined) return false;
    if (!('tokenizationInfo' in value) || value['tokenizationInfo'] === undefined) return false;
    if (!('totalVolume' in value) || value['totalVolume'] === undefined) return false;
    if (!('vib' in value) || value['vib'] === undefined) return false;
    if (!('website' in value) || value['website'] === undefined) return false;
    return true;
}

export function ProductFromJSON(json: any): Product {
    return ProductFromJSONTyped(json, false);
}

export function ProductFromJSONTyped(json: any, ignoreDiscriminator: boolean): Product {
    if (json == null) {
        return json;
    }
    return {
        
        'backToBack': DocumentFromJSON(json['back_to_back']),
        'bankAccountRequired': json['bank_account_required'],
        'calculateAccruedInterest': json['calculate_accrued_interest'],
        'createdAt': (new Date(json['created_at'])),
        'currencies': ((json['currencies'] as Array<any>).map(CurrencyRateFromJSON)),
        'distributionPlatforms': ((json['distribution_platforms'] as Array<any>).map(DistributionPlatformFromJSON)),
        'entryType': json['entry_type'],
        'ewpg': json['ewpg'],
        'id': json['id'],
        'investmentPaymentDeadline': json['investment_payment_deadline'],
        'isEcsp': json['is_ecsp'],
        'isTokenized': json['is_tokenized'],
        'isin': json['isin'],
        'issuanceDocument': VersionedIssuanceDocumentFromJSON(json['issuance_document']),
        'issuer': IssuerFromJSON(json['issuer']),
        'issuerDocuments': ((json['issuer_documents'] as Array<any>).map(DocumentFromJSON)),
        'latestProductPrice': MoneyFromJSON(json['latest_product_price']),
        'managed': json['managed'],
        'maxIssuableAmount': json['max_issuable_amount'],
        'name': json['name'],
        'nominalValue': MoneyFromJSON(json['nominal_value']),
        'permissions': ((json['permissions'] as Array<any>).map(ProductPermissionEnumFromJSON)),
        'productType': ProductTypeEnumFromJSON(json['product_type']),
        'publicationValid': json['publication_valid'],
        'remainingAmount': json['remaining_amount'],
        'securitiesDepositAccountRequired': json['securities_deposit_account_required'],
        'tokenType': json['token_type'],
        'tokenizationInfo': TokenContractInfoFromJSON(json['tokenization_info']),
        'totalVolume': json['total_volume'],
        'vib': DocumentFromJSON(json['vib']),
        'website': json['website'],
    };
}

export function ProductToJSON(value?: Omit<Product, 'bank_account_required'|'calculate_accrued_interest'|'created_at'|'currencies'|'distribution_platforms'|'entry_type'|'ewpg'|'id'|'investment_payment_deadline'|'is_ecsp'|'is_tokenized'|'isin'|'issuance_document'|'issuer_documents'|'latest_product_price'|'managed'|'name'|'permissions'|'product_type'|'publication_valid'|'remaining_amount'|'securities_deposit_account_required'|'token_type'|'total_volume'|'website'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'back_to_back': DocumentToJSON(value['backToBack']),
        'issuer': IssuerToJSON(value['issuer']),
        'max_issuable_amount': value['maxIssuableAmount'],
        'nominal_value': MoneyToJSON(value['nominalValue']),
        'tokenization_info': TokenContractInfoToJSON(value['tokenizationInfo']),
        'vib': DocumentToJSON(value['vib']),
    };
}

